import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import { ROLES } from '~/src/utils/constants';
import RolesDependent from '../RolesDependent';
import NavLink from './NavLink';
import kanbanIcon from '../../../static/img/kanbanIcon.svg';
import printerIcon from '../../../static/img/printerIcon.svg';
import proformaIcon from '../../../static/img/proformaIcon.svg';
import pencilIcon from '../../../static/img/pencilIcon.svg';
import rIcon from '../../../static/img/rIcon.svg';
import bulletIcon from '../../../static/img/bulletIcon.svg';
import trendUpIcon from '../../../static/img/trendUpIcon.svg';
import mathOperationsIcon from '../../../static/img/mathOperationsIcon.svg';
import SecondaryTabButton from '../forms/Button/SecondaryTabButton';

interface Prop {
  isConsolidation?: boolean;
}

interface ParamProps {
  id?: string;
}

export default function SecondaryNavigation({ isConsolidation }: Prop): React.ReactElement {
  const location = useLocation();
  const { id } = useParams<ParamProps>();
  const navigationData = [
    {
      title: 'Orders List',
      subTitle: '',
      link: '/orders-list',
      isSelected: location.pathname === '/orders-list',
      isDisabled: false,
      isConsolidation: isConsolidation,
      icon: bulletIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.OFFICE,
        ROLES.SALES,
        ROLES.DATABASE_ADMIN,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    {
      title: 'Order Edit',
      subTitle: '',
      link: `/orders/edit/${id}`,
      isSelected: location.pathname === `/orders/edit/${id}`,
      isDisabled: !id,
      isConsolidation: isConsolidation,
      icon: pencilIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.OFFICE,
        ROLES.SALES,
        ROLES.DATABASE_ADMIN,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    {
      title: 'Select Products',
      subTitle: '',
      link: isConsolidation ? `/orders/details/${id}/consolidate` : `/orders/details/${id}`,
      isSelected: location.pathname.includes(`/orders/details/${id}`),
      isDisabled: !id,
      icon: kanbanIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.SALES,
        ROLES.OFFICE,
        ROLES.DATABASE_ADMIN,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    {
      title: 'Order Calculations',
      subTitle: '',
      link: isConsolidation ? `/orders/calculations/${id}/${isConsolidation}` : `/orders/calculations/${id}`,
      isSelected: location.pathname === `/orders/calculations/${id}`,
      isDisabled: !id,
      isConsolidation: isConsolidation,
      icon: mathOperationsIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.OFFICE,
        ROLES.SALES,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.SVILA_CLIENT_ADMIN,
      ],
    },
    {
      title: 'Reserve Units',
      subTitle: '',
      link: isConsolidation ? `/orders/reserve/${id}/${isConsolidation}` : `/orders/reserve/${id}`,
      isSelected: location.pathname.includes(`/orders/reserve/${id}`),
      isDisabled: !id,
      icon: rIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.OFFICE,
        ROLES.SALES,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    {
      title: 'Status',
      subTitle: '',
      link: isConsolidation ? `/orders/status/${id}/${isConsolidation}` : `/orders/status/${id}`,
      isSelected: location.pathname.includes(`/orders/status/${id}`),
      isDisabled: !id,
      icon: kanbanIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.OFFICE,
        ROLES.SALES,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    {
      title: 'Proforma',
      subTitle: '',
      link: `/orders/proforma/${id}`,
      isSelected: location.pathname === `/orders/proforma/${id}`,
      isDisabled: !id,
      isConsolidation: isConsolidation,
      icon: proformaIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.SALES,
        ROLES.OFFICE,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    // {
    //   title: 'Order Printout',
    //   subTitle: '',
    //   link: isConsolidation ? `/orders/printout/${id}/${isConsolidation}` : `/orders/printout/${id}`,
    //   isSelected: location.pathname.includes(`/orders/printout/${id}`),
    //   isDisabled: !id,
    //   icon: printerIcon,
    //   roles: [ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SALES, ROLES.OFFICE],
    // },
    {
      title: 'Pallet-Numbering',
      subTitle: '',
      link: `/orders/pallet-numbering/${id}`,
      isSelected: location.pathname.includes(`/orders/pallet-numbering/${id}`),
      isDisabled: !id,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.SALES,
        ROLES.OFFICE,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
    {
      title: 'Pallets',
      subTitle: '',
      link: `/orders/pallet-description/${id}`,
      isSelected: location.pathname.includes(`/orders/pallet-description/${id}`),
      isDisabled: !id,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.SALES,
        ROLES.OFFICE,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },

    {
      title: 'Invoice Data',
      subTitle: '',
      link: isConsolidation ? `/orders/invoice/${id}/${isConsolidation}` : `/orders/invoice/${id}`,
      isSelected: location.pathname.includes(`/orders/invoice/${id}`),
      isDisabled: !id,
      icon: trendUpIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.SALES,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.OFFICE,
        ROLES.WAREHOUSE_EXTERNAL_USER,
        ROLES.SVILA_CLIENT_ADMIN,
        ROLES.SVILA_CLIENT
      ],
    },
  ];

  const navigationSmallButtonData = [
    {
      title: 'Order Printout',
      subTitle: '',
      link: isConsolidation ? `/orders/printout/${id}/${isConsolidation}` : `/orders/printout/${id}`,
      isSelected: location.pathname.includes(`/orders/printout/${id}`),
      isDisabled: !id,
      icon: printerIcon,
      roles: [
        ROLES.ADMIN,
        ROLES.SUPER_ADMIN,
        ROLES.WAREHOUSE,
        ROLES.WAREHOUSE_ADMIN,
        ROLES.SALES,
        ROLES.OFFICE,
        ROLES.WAREHOUSE_EXTERNAL_USER,
      ],
    },
  ];

  return (
    <div>
      <StyledSecondaryNavigation>
        {navigationData.map((item) => (
          <div key={uuid()}>
            {!item.isConsolidation === true && (
              <RolesDependent roles={item.roles}>
                <NavLink
                  title={item.title}
                  link={item.link}
                  isSelected={item.isSelected}
                  isDisabled={item.isDisabled}
                  subTitle={item.subTitle}
                  icon={item.icon}
                />
              </RolesDependent>
            )}
          </div>
        ))}
      </StyledSecondaryNavigation>
      <StyledSecondaryTabs>
        {navigationSmallButtonData.map((item) => (
          <div key={uuid()}>
            {!item.isConsolidation === true && (
              <RolesDependent roles={item.roles}>
                <SecondaryTabButton
                  title={item.title}
                  link={item.link}
                  isSelected={item.isSelected}
                  isDisabled={item.isDisabled}
                  icon={item.icon}
                />
              </RolesDependent>
            )}
          </div>
        ))}
      </StyledSecondaryTabs>
    </div>
  );
}

const StyledSecondaryTabs = styled.div`
  display: flex;
`;

const StyledSecondaryNavigation = styled.div`
  display: flex;

  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  margin: 10px 0;
  font-size: 14px;
  border-radius: 5px;
  div:first-child {
    div {
      a {
        border-top-left-radius: 10px !important;
        border-bottom-left-radius: 10px !important;
      }
    }
  }
  div:last-child {
    div {
      a {
        border-top-right-radius: 10px !important;
        border-bottom-right-radius: 10px !important;
      }
    }
  }
`;
